import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../services/api';
import { buildReceiptHtml } from '../utils/receiptTemplate';
import './TransactionReceipt.css';

const TransactionReceipt = () => {
  const { receiptNumber } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [sale, setSale] = useState(null);

  useEffect(() => {
    const fetchReceipt = async () => {
      try {
        setLoading(true);
        const res = await api.get(`/pos/sales/receipt/${encodeURIComponent(receiptNumber)}`);
        setSale(res.data);
      } catch (err) {
        toast.error('Failed to load receipt');
      } finally {
        setLoading(false);
      }
    };

    if (receiptNumber) fetchReceipt();
  }, [receiptNumber]);

  const receiptHtml = useMemo(() => {
    if (!sale) return null;

    // Map backend items -> format expected by receiptTemplate.js
    const mappedItems = (sale.items || []).map((it) => ({
      item_type: it.item_type,
      name: it.product_name || it.service_name || it.name,
      quantity: it.quantity,
      unit_price: it.unit_price,
      total_price: it.total_price
    }));

    return buildReceiptHtml({
      receipt_number: sale.receipt_number,
      sale_date: sale.sale_date,
      client_name:
        sale.client_name ||
        `${sale.client_first_name || ''} ${sale.client_last_name || ''}`.trim() ||
        'Walk-in Customer',
      cashier_name: sale.cashier_name,
      total_amount: sale.total_amount,
      discount: sale.discount,
      subtotal: sale.subtotal,
      tax: sale.tax,
      payment_method: sale.payment_method,
      amount_paid: sale.amount_paid,
      balance_due: sale.balance_due,
      payment_status: sale.payment_status,
      mpesa_confirmation_code: sale.mpesa_confirmation_code || null,
      items: mappedItems
    });
  }, [sale]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600" />
      </div>
    );
  }

  if (!sale) {
    return (
      <div className="space-y-4 p-6">
        <h1 className="text-2xl font-bold">Receipt not found</h1>
        <button
          onClick={() => navigate('/finance')}
          className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
        >
          Back to Finance
        </button>
      </div>
    );
  }

  const handlePrint = () => {
    if (!receiptHtml) return;

    // Print isolated receipt HTML via a hidden iframe.
    // This avoids overwriting the main SPA document (which can cause HMR/runtime errors and printing artifacts).
    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    iframe.setAttribute('aria-hidden', 'true');
    document.body.appendChild(iframe);

    const cleanup = () => {
      try {
        document.body.removeChild(iframe);
      } catch (e) {
        // ignore
      }
    };

    const onLoad = () => {
      try {
        iframe.contentWindow?.focus();
        iframe.contentWindow?.print();
      } finally {
        // Let print dialog open first.
        setTimeout(cleanup, 250);
      }
    };

    iframe.onload = onLoad;
    iframe.srcdoc = receiptHtml;
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Receipt: {sale.receipt_number}</h1>
          <p className="text-sm text-gray-500">View transaction details</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={handlePrint}
            className="bg-white border px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-gray-50"
          >
            Print Receipt
          </button>

          <button
            onClick={() => navigate('/finance')}
            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
          >
            Back
          </button>
        </div>
      </div>

      {/* Receipt Preview */}
      <div className="bg-white border rounded-xl shadow-sm overflow-hidden">
        <div
          className="receipt-preview"
          dangerouslySetInnerHTML={{ __html: receiptHtml }}
        />
      </div>
    </div>
  );
};

export default TransactionReceipt;

